/**
 * I18N-SUBSTRATE-1 — i18next initialization.
 *
 * Namespaces: common | tms | crm | wms | aegis | settings
 * Default locale: en (full). Other locales scaffolded; translations
 * are operator-zone (translation provider engagement).
 *
 * Lazy-load pattern: each namespace is loaded on demand via the
 * dynamic import backend. en/* is always available as fallback.
 */

import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

// Eagerly import en/* at bundle time — zero extra network round-trip for
// the default locale.
import enCommon   from '../locales/en/common.json';
import enTms      from '../locales/en/tms.json';
import enCrm      from '../locales/en/crm.json';
import enWms      from '../locales/en/wms.json';
import enAegis    from '../locales/en/aegis.json';
import enSettings from '../locales/en/settings.json';
import enIot      from '../locales/en/iot.json';

export const SUPPORTED_LOCALES = [
  { code: 'en',    label: 'English',             dir: 'ltr', flag: '🇺🇸' },
  { code: 'es',    label: 'Español',             dir: 'ltr', flag: '🇪🇸' },
  { code: 'fr',    label: 'Français',            dir: 'ltr', flag: '🇫🇷' },
  { code: 'ja',    label: '日本語',               dir: 'ltr', flag: '🇯🇵' },
  { code: 'zh',    label: '中文',                dir: 'ltr', flag: '🇨🇳' },
  { code: 'ar',    label: 'العربية',             dir: 'rtl', flag: '🇸🇦' },
  { code: 'he',    label: 'עברית',               dir: 'rtl', flag: '🇮🇱' },
];

export const RTL_LOCALES = new Set(
  SUPPORTED_LOCALES.filter(l => l.dir === 'rtl').map(l => l.code)
);

export const NAMESPACES = ['common', 'tms', 'crm', 'wms', 'aegis', 'settings', 'iot'];

export function isRtlLocale(locale) {
  return RTL_LOCALES.has(locale);
}

export function localeDir(locale) {
  return isRtlLocale(locale) ? 'rtl' : 'ltr';
}

/**
 * Lazy-load a non-en locale's namespace on demand.
 * Returns null if the locale is 'en' (already bundled) or on import error.
 */
async function loadLocaleNamespace(locale, ns) {
  if (locale === 'en') return null;
  try {
    const mod = await import(`../locales/${locale}/${ns}.json`);
    return mod.default ?? mod;
  } catch {
    return null;
  }
}

/**
 * Preload all namespaces for a given locale. Called by LocaleContext
 * when the user switches locale so the switch feels instant.
 */
export async function preloadLocale(locale) {
  if (locale === 'en') return;
  await Promise.all(
    NAMESPACES.map(async (ns) => {
      if (i18n.hasResourceBundle(locale, ns)) return;
      const bundle = await loadLocaleNamespace(locale, ns);
      if (bundle) i18n.addResourceBundle(locale, ns, bundle, true, true);
    })
  );
}

i18n
  .use(initReactI18next)
  .init({
    lng: 'en',
    fallbackLng: 'en',
    defaultNS: 'common',
    ns: NAMESPACES,
    resources: {
      en: {
        common:   enCommon,
        tms:      enTms,
        crm:      enCrm,
        wms:      enWms,
        aegis:    enAegis,
        settings: enSettings,
        iot:      enIot,
      },
    },
    interpolation: {
      escapeValue: false, // React already escapes
    },
    react: {
      useSuspense: false, // Kairos surfaces manage their own loading states
    },
  });

export default i18n;
